import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Copy, Check, Share2, X } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ShareProductModal — opens from the product detail page's "Share" button.
 * Generates `?ref=<tag>` URLs and one-click post buttons for X / Threads /
 * LinkedIn / Facebook + a hosted Copy Helper for Instagram.
 *
 * Ref tagging logic (priority order):
 *   1. If the current user is the creator of THIS product → `creator-{creator_id}`
 *      (most valuable signal — surfaces in admin "Top sharers" leaderboard)
 *   2. If user is logged in (any role) → their existing `referral_code` so
 *      they earn whatever the referral system pays
 *   3. Anonymous → `share-{6chars}` (still tracked, just no earner)
 */
export function ShareProductModal({ open, onOpenChange, product, creator, user }) {
  const [copied, setCopied] = useState(false);

  const refTag = useMemo(() => {
    if (!product) return '';
    if (user && creator && user.user_id === creator.user_id) {
      return `creator-${creator.creator_id}`;
    }
    if (user?.referral_code) {
      return user.referral_code;
    }
    if (user?.user_id) {
      // Logged-in user without a referral_code (older accounts) — still tag with their id
      return `user-${user.user_id.slice(-8)}`;
    }
    // Anonymous — generate a random share id for the link only (not stored)
    let cached = sessionStorage.getItem('anon_share_id');
    if (!cached) {
      cached = Math.random().toString(36).slice(2, 8);
      sessionStorage.setItem('anon_share_id', cached);
    }
    return `share-${cached}`;
  }, [product, creator, user]);

  const productUrl = useMemo(() => {
    if (!product) return '';
    const slug = product.slug || product.product_id;
    const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, '');
    // Use the production hostname when sharing — visitors won't recognize a preview URL.
    // Fall back to current origin for local/preview builds.
    const publicHost = process.env.REACT_APP_PUBLIC_URL || 'https://theailedger.ai';
    return `${publicHost}/product/${slug}?ref=${encodeURIComponent(refTag)}`;
  }, [product, refTag]);

  const shortSnippet = useMemo(() => {
    if (!product) return '';
    const desc = (product.description || '').replace(/\s+/g, ' ').trim();
    const truncated = desc.length > 140 ? desc.slice(0, 140) + '…' : desc;
    return `Just found this on theailedger.ai 🔥\n\n${product.name} by ${creator?.store_name || 'AI Creator'}\n${truncated}\n\n$${product.price} → ${productUrl}`;
  }, [product, creator, productUrl]);

  const longSnippet = useMemo(() => {
    if (!product) return '';
    const desc = (product.description || '').replace(/\s+/g, ' ').trim();
    return `Just discovered ${product.name} by ${creator?.store_name || 'an AI creator'} on The AI Ledger.\n\n${desc}\n\n$${product.price} — check it out: ${productUrl}`;
  }, [product, creator, productUrl]);

  const xUrl = useMemo(() => `https://twitter.com/intent/tweet?text=${encodeURIComponent(shortSnippet)}`, [shortSnippet]);
  const threadsUrl = useMemo(() => `https://www.threads.net/intent/post?text=${encodeURIComponent(shortSnippet)}`, [shortSnippet]);
  const linkedinUrl = useMemo(() => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(productUrl)}&summary=${encodeURIComponent(longSnippet)}`, [productUrl, longSnippet]);
  const facebookUrl = useMemo(() => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productUrl)}&quote=${encodeURIComponent(longSnippet)}`, [productUrl, longSnippet]);

  const copyHelperUrl = useMemo(() => {
    if (!shortSnippet) return '';
    const encoded = btoa(unescape(encodeURIComponent(shortSnippet))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    const publicHost = process.env.REACT_APP_PUBLIC_URL || 'https://theailedger.ai';
    return `${publicHost}/share-helper#${encoded}`;
  }, [shortSnippet]);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(productUrl);
    } catch {
      const ta = document.createElement('textarea'); ta.value = productUrl; document.body.appendChild(ta); ta.select(); try { document.execCommand('copy'); } catch (_) {} document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success('Link copied');
    setTimeout(() => setCopied(false), 2000);
  };

  // Outbound clicks (which network Jake/user picks) aren't tracked separately —
  // the meaningful signal is INBOUND attribution when a visitor lands on the
  // product page with ?ref=, which the backend already records via
  // /api/share/track/{ref} (called from ProductDetail on mount).

  const isOwnProduct = user && creator && user.user_id === creator.user_id;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-black border-white/10 text-white max-w-md" data-testid="share-product-modal">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <Share2 className="w-5 h-5 text-[#00F0FF]" /> Share this product
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Product preview */}
          <div className="bg-neutral-900 rounded-xl p-3 border border-white/5">
            <p className="font-semibold text-white truncate">{product?.name}</p>
            <p className="text-xs text-neutral-500 truncate">by {creator?.store_name || 'AI Creator'} · ${product?.price}</p>
          </div>

          {/* Earnings line — only when shareable for credit */}
          {isOwnProduct ? (
            <p className="text-xs text-[#00F0FF]" data-testid="share-creator-line">
              You're the creator. Sharing drives traffic + ranks you on the admin's "Top Sharers" board.
            </p>
          ) : user?.referral_code ? (
            <p className="text-xs text-[#00FF94]" data-testid="share-referral-line">
              Sharing earns you referral credit on any sale that converts within 30 days.
            </p>
          ) : null}

          {/* Native one-click buttons */}
          <div className="grid grid-cols-2 gap-2">
            <a href={xUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-black border border-white/15 hover:bg-white/5 rounded-lg py-3 text-sm font-bold text-white" data-testid="share-x-btn">
              <span className="text-base">𝕏</span> Post to X
            </a>
            <a href={threadsUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-black border border-white/15 hover:bg-white/5 rounded-lg py-3 text-sm font-bold text-white" data-testid="share-threads-btn">
              <span className="text-base">@</span> Threads
            </a>
            <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-[#0A66C2] hover:bg-[#0a5aa8] rounded-lg py-3 text-sm font-bold text-white" data-testid="share-linkedin-btn">
              <span>in</span> LinkedIn
            </a>
            <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 bg-[#1877F2] hover:bg-[#1668d8] rounded-lg py-3 text-sm font-bold text-white" data-testid="share-facebook-btn">
              <span>f</span> Facebook
            </a>
          </div>

          {/* IG copy helper + raw URL copy */}
          <div className="space-y-2">
            <a
              href={copyHelperUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 bg-[#00F0FF] hover:bg-[#00D4E0] text-black rounded-lg py-3 text-sm font-bold"
              data-testid="share-instagram-copy-btn"
            >
              <Copy className="w-4 h-4" /> Copy for Instagram
            </a>
            <button
              type="button"
              onClick={handleCopyLink}
              className={`w-full flex items-center justify-center gap-2 ${copied ? 'bg-[#00FF94] text-black' : 'bg-neutral-800 hover:bg-neutral-700 text-white'} rounded-lg py-3 text-sm font-bold transition-colors`}
              data-testid="share-copy-link-btn"
            >
              {copied ? <><Check className="w-4 h-4" /> Link copied</> : <><Copy className="w-4 h-4" /> Copy link only</>}
            </button>
          </div>

          <p className="text-[11px] text-neutral-600 text-center pt-1">
            All share links include a tracking tag so attributed traffic + sales surface in your dashboard.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default ShareProductModal;
